
import moment from 'moment';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useApi } from '../composables/useApi';
import { validationFieldsFrom } from '../utils/csvRowError';
import SnackbarComposables from '../composables/SnackbarComposables';
import { getCurrentInstance } from 'vue';
import { store as rootStore } from '../store';
import rootRouter from '../routing/router';
import rootI18n from '../i18n';

const ProductService = () => 
{
    /* Data */
    const hasInstance = !!getCurrentInstance();
    const store = hasInstance ? useStore() : rootStore;
    const router = hasInstance ? useRouter() : rootRouter;
    const ApiClient = useApi();

    let t, locale, addSnackbar;
    if (hasInstance)
    {
        const i18n = useI18n();
        t = i18n.t;
        locale = i18n.locale;
        const snackbar = SnackbarComposables();
        addSnackbar = snackbar.addSnackbar;
    }
    else
    {
        t = (key, ...args) => rootI18n.global.t(key, ...args);
        locale = { value: rootStore.state.locale };
        addSnackbar = () => {};
    }

    /* Methods */
    const indexProduct = async(date= moment().format('YYYY-MM-DD'), orderType = 'desc', orderBy, input, status="all", perPage = 15,  paginated = true) => 
    {
        store.commit('errors/CLEAR_STATE');
        let endpoint = `product/?date=${date}&orderType=${orderType}&status=${status}&paginated=${paginated}&per_page=${perPage}`;
        if(orderBy)
        {
            endpoint = endpoint+`&orderBy=${orderBy}`
        }
        if(input)
        {
            endpoint = endpoint+`&input=${input}`
        }

        const res = await ApiClient.get(endpoint);
        if(res)
        {
            return res.data;
        }
    };
    const storeProduct = async(payload) => 
    {
        store.commit('errors/CLEAR_STATE');
        const res = await ApiClient.post('product/store', payload);
        if(res.data) return res.data;
        return res
    };
    const storeFromCSV = async(payload) => 
    {
        store.commit('errors/CLEAR_STATE');
        const res = await ApiClient.post('product/store-from-csv', payload);
        if(res.data) return res.data;
        /*
         * Un 422 non torna come risposta: l'interceptor lo assorbe e **risolve**
         * col numero `422`, depositando il corpo in `errors`. Senza rileggerlo
         * la riga dell'import risulta rossa e muta, perche' `res.message` su un
         * numero e' `undefined` — e il motivo del rifiuto e' proprio la cella
         * che l'utente deve correggere.
         *
         * Lo stato e' stato azzerato una riga sopra e l'import invia **una riga
         * per volta**, aspettando ogni risposta: quello che c'e' dentro adesso
         * appartiene a questa riga e non a un'altra.
         */
        if(res === 422) return {
            status: 'error',
            validation_errors: validationFieldsFrom(store.state.errors.errors)
        };
        return res;
    };
    const storeExactMatch = async(product, payload) => 
    {
        store.commit('errors/CLEAR_STATE');
        const res = await ApiClient.post(`product/store-match/${product}`, payload);
        if(res) return res;
    };
    const showProduct = async(product) => 
    {
        store.commit('errors/CLEAR_STATE');
        let res = await ApiClient.get(`product/show/${product}`);
        if(res.data?.product) return res.data
        return router.push(`/${store.state.locale}/notFound`);
    };
    const updateProduct = async(product, payload) => 
    {
        store.commit('errors/CLEAR_STATE');
        const res = await ApiClient.put(`product/update/${product}`, payload);
        if(res)
        {
            if(res.status === 200) 
            {
                addSnackbar('success', t('product.update.updatedMessage', store.state.locale));
                router.push(`/${store.state.locale}/dashboard`);
                return 200;
            }
            else 
            {
                addSnackbar('error', t('snackbar.refresh', store.state.locale));
            }
            return res;
        }
    };
    const destroyProduct = async(product) =>
    {
        store.commit('errors/CLEAR_STATE');
        const res = await ApiClient.delete(`product/delete/${product}`);
        if(res)
        {
            if(res.status === 200)
            {
                addSnackbar('success', t('product.delete.deletedMessage', store.state.locale));
                router.push(`/${store.state.locale}/dashboard`);
                return 200;
            }
            else 
            {
                addSnackbar('error', t('snackbar.refresh', store.state.locale));
            }
            return res;
        }      
    };
    const searchExactMatch = async(product) => 
    {
        store.commit('errors/CLEAR_STATE');
        const res = await ApiClient.get(`product/search-match/${product}`);
        if(res)
        {
            if(res.data?.status)
            {
                return res.data;
            }
            else 
            {
                return res;
            }
        }
    };
    const fetchProduct = async (endpoint) => 
    {
        store.commit('errors/CLEAR_STATE');
        const res = await ApiClient.get(endpoint);
        if(res)
        { 
            if(res.data)  
            {
                return res.data;
            }
            return res;
        }
    };

    const indexProductWithAssociations = async(date= moment().format('YYYY-MM-DD')) => 
    {
        store.commit('errors/CLEAR_STATE');
        const res = await ApiClient.get(`product/associations?date=${date}`);
        if(res)
        {
            return res.data;
        }
    };

    const clearProduct = async(product, payload) => 
    {
        store.commit('errors/CLEAR_STATE');
        const res = await ApiClient.put(`product/clear-product/${product}`, payload);
        if(res.data) return res.data;
        return res;
    };


    /* Hooks */
    locale.value = store.state.locale;

    return {
        /* Methods */
        indexProduct,
        storeProduct,
        storeFromCSV,
        storeExactMatch,
        showProduct,
        updateProduct,
        destroyProduct,
        searchExactMatch,
        fetchProduct,
        indexProductWithAssociations,
        clearProduct
    }
};

export default ProductService;
